import React, { useContext, useState, useEffect, useRef } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../assets/context/AuthContext';
import { QuoteContext } from '../assets/context/QuoteContext';
import logo from '../assets/CourierLink-Header-Logo.png'
import url from './url';

const ACCOUNT_LABEL = { customer: 'Customer', business: 'Business account' };

function ProfileDropdown({ profile, accountType, onLogout, onClose }) {
    const initials = profile?.Name
        ? profile.Name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
        : '?';

    return (
        <div className="nav-profile-dropdown" role="menu">
            {/* Identity */}
            <div className="nav-profile-header">
                <div className="nav-profile-avatar nav-profile-avatar-lg">{initials}</div>
                <div>
                    <strong className="nav-profile-name">{profile?.Name || '—'}</strong>
                    <span className="nav-profile-email">{profile?.Email || '—'}</span>
                    <span className={`nav-profile-badge ${accountType === 'business' ? 'nav-profile-badge-business' : ''}`}>
                        {ACCOUNT_LABEL[accountType] || accountType}
                    </span>
                </div>
            </div>

            <div className="nav-profile-divider" />

            <NavLink to="/account" className="nav-profile-item" onClick={onClose} role="menuitem">
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                Account settings
            </NavLink>

            <div className="nav-profile-divider" />

            <button type="button" className="nav-profile-item nav-profile-signout" onClick={onLogout} role="menuitem">
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Log out
            </button>
        </div>
    );
}

function Navbar() {
    const { isLoggedIn, isAdmin, accountType, logout } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const { resetBookingFlow } = useContext(QuoteContext);
    const dropdownRef = useRef(null);

    // Fetch name + email when a regular user logs in
    useEffect(() => {
        if (isLoggedIn && !isAdmin) {
            fetch(`${url}/api/customer/profile`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
            })
                .then((r) => r.ok ? r.json() : null)
                .then((data) => { if (data) setProfile(data); })
                .catch(() => {});
        } else {
            setProfile(null);
        }
    }, [isLoggedIn, isAdmin]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!profileOpen) return;
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [profileOpen]);

    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        setProfileOpen(false);
        setProfile(null);
        navigate(isAdmin ? '/AdminLogin' : '/Login');
    };

    const closeMenu = () => setMenuOpen(false);
    const startNewBooking = () => { resetBookingFlow(); closeMenu(); };

    const initials = profile?.Name
        ? profile.Name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
        : '?';

    return (
        <>
            <div className={`container nav-shell ${isAdmin ? 'admin-nav-shell' : ''}`}>
                <nav className="navbar" aria-label="Primary navigation">
                    <NavLink className="navbar-brand" to="/" onClick={closeMenu}>
                        <img src={logo} alt="CourierLink" />
                    </NavLink>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-controls="primary-menu"
                        aria-expanded={menuOpen}
                        aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    >
                        <span></span><span></span><span></span>
                    </button>
                    <div className={`navbar-menu ${menuOpen ? 'is-open' : ''}`} id="primary-menu">
                        <div className="navbar-links">
                            <NavLink className="nav-link" to="/" onClick={closeMenu}>Home</NavLink>
                            <NavLink className="nav-link" to="/faq" onClick={closeMenu}>FAQs</NavLink>
                            <NavLink className="nav-link" to="/Tracking" onClick={closeMenu}>Track shipment</NavLink>
                            {isAdmin ? (
                                <NavLink className="nav-link" to="/Dashboard" onClick={closeMenu}>Admin dashboard</NavLink>
                            ) : (
                                isLoggedIn && <NavLink className="nav-link" to="/Bookings" onClick={closeMenu}>{accountType === 'business' ? 'Dashboard' : 'My bookings'}</NavLink>
                            )}
                        </div>
                        <div className="top-cta">
                            {isAdmin ? (
                                <>
                                    <NavLink to="/Dashboard" className="btn btn-smth" onClick={closeMenu}>Open dashboard <span aria-hidden="true">↗</span></NavLink>
                                    <button onClick={handleLogout} className="btn btn-stroked">Sign out</button>
                                </>
                            ) : isLoggedIn ? (
                                <>
                                    <NavLink to="/CalQuote" className="btn btn-smth" onClick={startNewBooking}>Create booking <span aria-hidden="true">↗</span></NavLink>

                                    {/* Profile button + dropdown */}
                                    <div className="nav-profile-wrap" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            className="nav-profile-trigger"
                                            onClick={() => setProfileOpen((o) => !o)}
                                            aria-expanded={profileOpen}
                                            aria-haspopup="true"
                                            aria-label="Account menu"
                                        >
                                            <span className="nav-profile-avatar">{initials}</span>
                                            <span className="nav-profile-trigger-name">
                                                {profile?.Name?.split(' ')[0] || 'Account'}
                                            </span>
                                            <svg
                                                aria-hidden="true"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14" height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ transform: profileOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </button>

                                        {profileOpen && (
                                            <ProfileDropdown
                                                profile={profile}
                                                accountType={accountType}
                                                onLogout={handleLogout}
                                                onClose={() => { setProfileOpen(false); closeMenu(); }}
                                            />
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <NavLink to="/Login" className="nav-login" onClick={closeMenu}>Log in</NavLink>
                                    <NavLink to="/CalQuote" className="btn btn-smth" onClick={startNewBooking}>Get a quote <span aria-hidden="true">↗</span></NavLink>
                                </>
                            )}
                        </div>
                    </div>
                </nav>
            </div>
            <Outlet />
        </>
    )
}

export default Navbar;
